import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CalendarRange, Printer, Eye, CheckCircle2, Search, FileDown } from "lucide-react";
import { format } from "date-fns";
import { ar } from "date-fns/locale";
import { toast } from "sonner";
import { breakdownInclusive, EGYPT_VAT_RATE, formatCurrency, CURRENCY_SYMBOL } from "@/lib/currency";
import SubscriptionInvoiceDialog, { SubscriptionInvoiceData } from "@/components/accountant/SubscriptionInvoiceDialog";

const PLAN_LABELS: Record<string, string> = {
  basic: "Starter",
  standard: "Growth",
  premium: "Pro",
  free: "مجاني",
  enterprise: "مؤسسي",
};

const MONTHS_AR = [
  "يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو",
  "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر",
];

type StatusKey = "paid" | "unpaid" | "overdue" | "missing";

const STATUS_META: Record<StatusKey, { label: string; cls: string }> = {
  paid:    { label: "مدفوعة",    cls: "bg-green-100 text-green-800 border-green-300" },
  unpaid:  { label: "غير مدفوعة", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  overdue: { label: "متأخرة",    cls: "bg-red-100 text-red-700 border-red-300" },
  missing: { label: "بدون فاتورة", cls: "bg-gray-100 text-gray-700 border-gray-300" },
};

const computeStatus = (inv: any): StatusKey => {
  if (!inv) return "missing";
  if (inv.status === "paid") return "paid";
  if (inv.due_date && new Date(inv.due_date) < new Date()) return "overdue";
  return "unpaid";
};

const now = new Date();

const AccountantMonthlyStatus = () => {
  const qc = useQueryClient();
  const [year, setYear] = useState<number>(now.getFullYear());
  const [month, setMonth] = useState<number>(now.getMonth() + 1);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState<"all" | StatusKey>("all");
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<SubscriptionInvoiceData | null>(null);

  const { data: nurseries } = useQuery({
    queryKey: ["accountant-nurseries-list"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("nurseries")
        .select("id, name, address, tax_number")
        .order("name");
      if (error) throw error;
      return data || [];
    },
  });

  const { data: subs } = useQuery({
    queryKey: ["accountant-subs-list"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("nursery_subscriptions" as any)
        .select("id, nursery_id, plan, billing_cycle, status");
      if (error) throw error;
      return (data || []) as any[];
    },
  });

  const { data: invoices, isLoading } = useQuery({
    queryKey: ["accountant-monthly-invoices", year, month],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("nursery_subscription_invoices" as any)
        .select("*")
        .eq("year", year)
        .eq("month", month);
      if (error) throw error;
      return (data || []) as any[];
    },
  });

  const markPaid = useMutation({
    mutationFn: async (id: string) => {
      const { data: u } = await supabase.auth.getUser();
      const { error } = await supabase
        .from("nursery_subscription_invoices" as any)
        .update({
          status: "paid",
          paid_at: new Date().toISOString(),
          confirmed_by: u?.user?.id ?? null,
          confirmed_at: new Date().toISOString(),
        })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("تم تأكيد التحصيل ✓");
      qc.invalidateQueries({ queryKey: ["accountant-monthly-invoices", year, month] });
    },
    onError: (e: any) => toast.error(e?.message || "فشل تأكيد التحصيل"),
  });

  const rows = useMemo(() => {
    const invByNursery = new Map<string, any>();
    (invoices || []).forEach((i) => invByNursery.set(i.nursery_id, i));
    const subByNursery = new Map<string, any>();
    (subs || []).forEach((s) => subByNursery.set(s.nursery_id, s));
    return (nurseries || []).map((n: any) => {
      const inv = invByNursery.get(n.id);
      const sub = subByNursery.get(n.id);
      return {
        nursery: n,
        invoice: inv,
        subscription: sub,
        status: computeStatus(inv) as StatusKey,
        amount: Number(inv?.amount || 0),
      };
    });
  }, [nurseries, invoices, subs]);

  const filtered = rows.filter((r) => {
    if (filterStatus !== "all" && r.status !== filterStatus) return false;
    if (search && !r.nursery.name?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const totals = useMemo(() => {
    const t = { paid: 0, unpaid: 0, overdue: 0, missing: 0, gross: 0, collected: 0 };
    filtered.forEach((r) => {
      t[r.status] += 1;
      t.gross += r.amount;
      if (r.status === "paid") t.collected += r.amount;
    });
    return t;
  }, [filtered]);

  const openDetails = (r: any) => {
    if (!r.invoice) return;
    setSelected({
      id: r.invoice.id,
      nursery_name: r.nursery.name,
      nursery_address: r.nursery.address,
      nursery_tax_number: r.nursery.tax_number,
      plan_label: PLAN_LABELS[r.subscription?.plan] || r.subscription?.plan || "اشتراك",
      billing_cycle: r.subscription?.billing_cycle,
      pricing_model: r.invoice.pricing_model,
      children_count: r.invoice.children_count,
      price_per_child: r.invoice.price_per_child,
      month: r.invoice.month,
      year: r.invoice.year,
      amount: Number(r.invoice.amount || 0),
      vat_rate: Number(r.invoice.vat_rate ?? EGYPT_VAT_RATE),
      status: r.invoice.status,
      due_date: r.invoice.due_date,
      paid_at: r.invoice.paid_at,
      created_at: r.invoice.created_at,
    });
    setOpen(true);
  };

  const handlePrintAll = () => window.print();

  const handleExportCSV = () => {
    const header = ["الحضانة", "الباقة", "الدورة", "المبلغ (ج.م)", "تاريخ الاستحقاق", "الحالة", "تاريخ السداد"];
    const lines = filtered.map((r) => {
      return [
        r.nursery.name,
        PLAN_LABELS[r.subscription?.plan] || r.subscription?.plan || "—",
        r.subscription?.billing_cycle === "yearly" ? "سنوي" : "شهري",
        r.amount.toFixed(2),
        r.invoice?.due_date ? format(new Date(r.invoice.due_date), "yyyy-MM-dd") : "—",
        STATUS_META[r.status].label,
        r.invoice?.paid_at ? format(new Date(r.invoice.paid_at), "yyyy-MM-dd") : "—",
      ].map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",");
    });
    const csv = "\uFEFF" + [header.join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `subscription-status-${year}-${String(month).padStart(2, "0")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("تم تنزيل CSV");
  };

  const yearOptions = Array.from({ length: 6 }, (_, i) => now.getFullYear() - 2 + i);

  return (
    <div className="space-y-6 print:space-y-3" dir="rtl">
      <div className="flex flex-wrap items-start justify-between gap-3 print:hidden">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <CalendarRange className="w-6 h-6 text-primary" />
            حالة فواتير اشتراكات الحضانات الشهرية
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            عرض حالة الاشتراك لكل حضانة عن شهر محدد (مدفوع / غير مدفوع / متأخر) مع الطباعة والتصدير.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="gap-1" onClick={handleExportCSV}>
            <FileDown className="w-4 h-4" /> CSV
          </Button>
          <Button size="sm" className="gap-1" onClick={handlePrintAll}>
            <Printer className="w-4 h-4" /> طباعة
          </Button>
        </div>
      </div>

      {/* Print header */}
      <div className="hidden print:block text-center border-b pb-2">
        <h2 className="text-lg font-bold">تقرير حالة فواتير الاشتراكات</h2>
        <p className="text-sm">{MONTHS_AR[month - 1]} {year}</p>
      </div>

      {/* Filters */}
      <Card className="print:hidden">
        <CardContent className="p-4">
          <div className="grid md:grid-cols-5 gap-3">
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">الشهر</label>
              <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MONTHS_AR.map((m, i) => (
                    <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">السنة</label>
              <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {yearOptions.map((y) => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">الحالة</label>
              <Select value={filterStatus} onValueChange={(v) => setFilterStatus(v as any)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">جميع الحالات</SelectItem>
                  <SelectItem value="paid">مدفوعة</SelectItem>
                  <SelectItem value="unpaid">غير مدفوعة</SelectItem>
                  <SelectItem value="overdue">متأخرة</SelectItem>
                  <SelectItem value="missing">بدون فاتورة</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <label className="text-xs text-muted-foreground mb-1 block">بحث</label>
              <div className="relative">
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input placeholder="اسم الحضانة..." value={search} onChange={(e) => setSearch(e.target.value)} className="pr-9" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Card><CardContent className="p-3 text-center">
          <p className="text-xs text-muted-foreground">الحضانات</p>
          <p className="text-xl font-bold">{filtered.length}</p>
        </CardContent></Card>
        <Card><CardContent className="p-3 text-center">
          <p className="text-xs text-muted-foreground">مدفوعة</p>
          <p className="text-xl font-bold text-green-600">{totals.paid}</p>
        </CardContent></Card>
        <Card><CardContent className="p-3 text-center">
          <p className="text-xs text-muted-foreground">غير مدفوعة</p>
          <p className="text-xl font-bold text-amber-600">{totals.unpaid}</p>
        </CardContent></Card>
        <Card><CardContent className="p-3 text-center">
          <p className="text-xs text-muted-foreground">متأخرة</p>
          <p className="text-xl font-bold text-red-600">{totals.overdue}</p>
        </CardContent></Card>
        <Card><CardContent className="p-3 text-center">
          <p className="text-xs text-muted-foreground">إجمالي</p>
          <p className="text-base font-bold">{formatCurrency(totals.gross)}</p>
        </CardContent></Card>
        <Card><CardContent className="p-3 text-center">
          <p className="text-xs text-muted-foreground">المحصّل</p>
          <p className="text-base font-bold text-green-600">{formatCurrency(totals.collected)}</p>
        </CardContent></Card>
      </div>

      {/* Table */}
      <Card>
        <CardHeader className="print:hidden">
          <CardTitle className="text-base">
            {MONTHS_AR[month - 1]} {year} — {filtered.length} حضانة
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-center py-8 text-muted-foreground">جاري التحميل...</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>الحضانة</TableHead>
                    <TableHead>الباقة</TableHead>
                    <TableHead>المبلغ</TableHead>
                    <TableHead>الاستحقاق</TableHead>
                    <TableHead>الحالة</TableHead>
                    <TableHead>تاريخ السداد</TableHead>
                    <TableHead className="print:hidden">إجراءات</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((r) => {
                    const meta = STATUS_META[r.status];
                    return (
                      <TableRow key={r.nursery.id}>
                        <TableCell className="font-medium">{r.nursery.name}</TableCell>
                        <TableCell className="text-xs">
                          <Badge variant="outline">
                            {PLAN_LABELS[r.subscription?.plan] || r.subscription?.plan || "—"}
                          </Badge>
                          {r.subscription?.billing_cycle && (
                            <span className="text-muted-foreground mr-1">
                              ({r.subscription.billing_cycle === "yearly" ? "سنوي" : "شهري"})
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="font-bold">
                          {r.invoice ? `${r.amount.toLocaleString("ar-EG")} ${CURRENCY_SYMBOL}` : "—"}
                        </TableCell>
                        <TableCell className="text-xs">
                          {r.invoice?.due_date
                            ? format(new Date(r.invoice.due_date), "dd MMM yyyy", { locale: ar })
                            : "—"}
                        </TableCell>
                        <TableCell>
                          <Badge className={meta.cls}>{meta.label}</Badge>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {r.invoice?.paid_at
                            ? format(new Date(r.invoice.paid_at), "dd MMM yyyy", { locale: ar })
                            : "—"}
                        </TableCell>
                        <TableCell className="print:hidden">
                          <div className="flex items-center gap-1">
                            {r.invoice && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 gap-1 text-xs"
                                onClick={() => openDetails(r)}
                              >
                                <Eye className="w-3 h-3" /> عرض/طباعة
                              </Button>
                            )}
                            {r.invoice && r.status !== "paid" && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 gap-1 text-xs text-green-700 border-green-300 hover:bg-green-50"
                                onClick={() => markPaid.mutate(r.invoice.id)}
                                disabled={markPaid.isPending}
                              >
                                <CheckCircle2 className="w-3 h-3" /> تأكيد دفع
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {!filtered.length && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        لا توجد بيانات
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <SubscriptionInvoiceDialog open={open} onOpenChange={setOpen} invoice={selected} />
    </div>
  );
};

export default AccountantMonthlyStatus;
